/**
 * CoachPlaybook - Lesson Library Component
 * Version: 0.6.0
 * Description: Browse plays for learning — grouped by sport, searchable, with difficulty badges
 */

import React, { useState, useMemo } from 'react';

/**
 * Calculate difficulty from step count
 */
const getDifficulty = (play) => {
  const stepCount = (play.steps?.length || 0) + (play.paths?.length || 0);
  if (stepCount <= 3) return { label: 'Beginner', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' };
  if (stepCount <= 6) return { label: 'Intermediate', color: '#fbbf24', bg: 'rgba(251,191,36,0.15)' };
  return { label: 'Advanced', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' };
};

/**
 * Get sport config by ID from the sports array
 */
const getSportForPlay = (play, sports) => {
  if (!sports || sports.length === 0) return null;
  const sportId = play.sport || play.sportId || 'basketball';
  return sports.find(s => s.id === sportId) || sports[0] || null;
};

const LessonLibrary = ({ plays = [], sports = [], onStartQuiz }) => {
  const [search, setSearch] = useState('');
  const [filterSport, setFilterSport] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');

  const filteredPlays = useMemo(() => {
    let result = [...plays];

    // Search filter
    if (search.trim()) {
      const term = search.toLowerCase().trim();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(term) ||
        p.type?.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term) ||
        p.notes?.toLowerCase().includes(term)
      );
    }

    // Sport filter
    if (filterSport !== 'all') {
      result = result.filter(p => (p.sport || p.sportId || 'basketball') === filterSport);
    }

    // Type filter
    if (filterType !== 'all') {
      result = result.filter(p => p.type === filterType);
    }

    // Difficulty filter
    if (filterDifficulty !== 'all') {
      result = result.filter(p => getDifficulty(p).label.toLowerCase() === filterDifficulty);
    }

    return result;
  }, [plays, search, filterSport, filterType, filterDifficulty]);

  // Group by sport
  const groupedPlays = useMemo(() => {
    const groups = {};
    for (const play of filteredPlays) {
      const sportId = play.sport || play.sportId || 'basketball';
      if (!groups[sportId]) groups[sportId] = [];
      groups[sportId].push(play);
    }
    return groups;
  }, [filteredPlays]);

  const uniqueTypes = useMemo(() => {
    const types = new Set(plays.map(p => p.type).filter(Boolean));
    return Array.from(types);
  }, [plays]);

  const styles = {
    container: {
      height: '100%',
      overflowY: 'auto',
      backgroundColor: '#0f172a',
      color: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    inner: {
      padding: '24px',
      maxWidth: '960px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '900',
      color: '#f1f5f9',
      marginBottom: '4px'
    },
    subtitle: {
      fontSize: '14px',
      color: '#94a3b8'
    },
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginBottom: '24px'
    },
    searchInput: {
      flex: '1 1 200px',
      padding: '10px 14px',
      borderRadius: '10px',
      border: '2px solid #334155',
      backgroundColor: '#1e293b',
      color: '#f1f5f9',
      fontSize: '14px',
      outline: 'none'
    },
    select: {
      padding: '10px 14px',
      borderRadius: '10px',
      border: '2px solid #334155',
      backgroundColor: '#1e293b',
      color: '#f1f5f9',
      fontSize: '13px',
      outline: 'none',
      cursor: 'pointer',
      minWidth: '130px'
    },
    groupHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '14px',
      marginTop: '20px'
    },
    groupTitle: {
      fontSize: '18px',
      fontWeight: '800',
      color: '#e2e8f0'
    },
    groupBadge: {
      fontSize: '12px',
      fontWeight: '700',
      color: '#94a3b8',
      backgroundColor: '#334155',
      padding: '2px 10px',
      borderRadius: '999px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: '14px',
      marginBottom: '16px'
    },
    card: {
      backgroundColor: '#1e293b',
      borderRadius: '14px',
      border: '2px solid #334155',
      padding: '18px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      transition: 'border-color 0.2s ease, transform 0.15s ease',
      cursor: 'default'
    },
    cardTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    },
    playName: {
      fontSize: '16px',
      fontWeight: '800',
      color: '#f1f5f9',
      lineHeight: '1.3'
    },
    sportIcon: {
      fontSize: '22px',
      flexShrink: 0
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap'
    },
    badge: (color, bg) => ({
      fontSize: '11px',
      fontWeight: '700',
      color,
      backgroundColor: bg,
      padding: '3px 10px',
      borderRadius: '999px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }),
    typeBadge: {
      fontSize: '11px',
      fontWeight: '600',
      color: '#94a3b8',
      backgroundColor: '#0f172a',
      padding: '3px 10px',
      borderRadius: '999px'
    },
    stepCount: {
      fontSize: '12px',
      color: '#64748b',
      fontWeight: '600'
    },
    startButton: {
      marginTop: 'auto',
      padding: '10px 16px',
      borderRadius: '10px',
      border: 'none',
      backgroundColor: '#3b82f6',
      color: '#fff',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      textAlign: 'center'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      color: '#94a3b8',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Lesson Library</h1>
          <p style={styles.subtitle}>Select a play to quiz yourself on. Test your knowledge of each play's movements and actions.</p>
        </div>

        {/* Filters */}
        <div style={styles.filters}>
          <input
            type="text"
            placeholder="Search plays..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
          {sports.length > 1 && (
            <select
              value={filterSport}
              onChange={(e) => setFilterSport(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Sports</option>
              {sports.map(s => (
                <option key={s.id} value={s.id}>{s.icon || ''} {s.name}</option>
              ))}
            </select>
          )}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Types</option>
            {uniqueTypes.map(t => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {/* Empty state */}
        {filteredPlays.length === 0 && (
          <div style={styles.emptyState}>
            <p style={{ fontSize: '48px', marginBottom: '16px' }}>📚</p>
            <p style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0', marginBottom: '8px' }}>
              {plays.length === 0 ? 'No Plays Available' : 'No Matching Plays'}
            </p>
            <p style={{ fontSize: '14px' }}>
              {plays.length === 0
                ? 'Create some plays in the Playbook first, then come back to quiz yourself.'
                : 'Try adjusting your search or filters.'}
            </p>
          </div>
        )}

        {/* Grouped play cards */}
        {Object.entries(groupedPlays).map(([sportId, sportPlays]) => {
          const sportConfig = sports.find(s => s.id === sportId) || { name: sportId, icon: '' };
          return (
            <div key={sportId}>
              <div style={styles.groupHeader}>
                <span style={styles.sportIcon}>{sportConfig.icon || ''}</span>
                <span style={styles.groupTitle}>{sportConfig.name || sportId}</span>
                <span style={styles.groupBadge}>{sportPlays.length} {sportPlays.length === 1 ? 'play' : 'plays'}</span>
              </div>
              <div style={styles.grid}>
                {sportPlays.map(play => {
                  const difficulty = getDifficulty(play);
                  const stepCount = (play.steps?.length || 0) + (play.paths?.length || 0);
                  return (
                    <div
                      key={play.id}
                      style={styles.card}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <div style={styles.cardTop}>
                        <span style={styles.playName}>{play.name || 'Untitled Play'}</span>
                        <span style={styles.sportIcon}>{sportConfig.icon || ''}</span>
                      </div>
                      <div style={styles.meta}>
                        <span style={styles.badge(difficulty.color, difficulty.bg)}>{difficulty.label}</span>
                        {play.type && (
                          <span style={styles.typeBadge}>{play.type}</span>
                        )}
                        <span style={styles.stepCount}>{stepCount} {stepCount === 1 ? 'step' : 'steps'}</span>
                      </div>
                      <button
                        style={styles.startButton}
                        onClick={() => onStartQuiz && onStartQuiz(play)}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                      >
                        Start Quiz
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LessonLibrary;
